import type { SearchCredentials } from '@shared/core/search';
import { z } from 'zod';

import { withRequestTimeout } from '../transport/timeout';

const SEARCH_CREDENTIALS_ENDPOINT = '/.redwood/functions/searchCredentials';
const CREDENTIALS_TIMEOUT_MS = 10_000;

export interface EntitledSearchCredentials extends SearchCredentials {
  readonly collections?: readonly string[];
}

const credentialsSchema = z.object({
  engine: z.enum(['algolia', 'typesense']),
  endpoint: z.string(),
  keys: z.object({ general: z.string().nullable(), orders: z.string().optional() }),
  collections: z.array(z.string()).optional(),
  expiresAt: z.string(),
});

export class SearchCredentialsHttpError extends Error {
  constructor(readonly status: number) {
    super(`searchCredentials responded ${status}`);
    this.name = 'SearchCredentialsHttpError';
  }
}

const TRANSIENT_CLIENT_STATUSES = new Set([408, 429]);

export const isPermanentCredentialsFailure = (error: unknown): boolean =>
  error instanceof SearchCredentialsHttpError &&
  error.status >= 400 &&
  error.status < 500 &&
  !TRANSIENT_CLIENT_STATUSES.has(error.status);

export async function fetchSearchCredentials(
  getToken: () => Promise<string | null>
): Promise<EntitledSearchCredentials> {
  const jwt = await getToken();
  const response = await fetch(SEARCH_CREDENTIALS_ENDPOINT, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'auth-provider': 'clerk',
      ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
    },
    signal: withRequestTimeout(undefined, CREDENTIALS_TIMEOUT_MS),
  });
  if (!response.ok) throw new SearchCredentialsHttpError(response.status);
  return credentialsSchema.parse(await response.json());
}
